import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { 
  Sprout, CheckCircle, Circle, Clock, AlertCircle, ChevronRight, 
  Loader2, ArrowLeft, Send
} from 'lucide-react';
import { getOnboardingStatus } from '../services/api';
import { useAuth } from '../context/AuthContext';

const statusConfig = {
  not_started: { label: 'Not Started', color: 'bg-gray-100 text-gray-600', icon: Circle },
  in_progress: { label: 'In Progress', color: 'bg-blue-100 text-blue-700', icon: Clock },
  submitted: { label: 'Submitted', color: 'bg-amber-100 text-amber-700', icon: Send },
  approved: { label: 'Approved', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
};

const OnboardingDashboard = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user } = useAuth();

  const fetchStatus = useCallback(async () => {
    try {
      const res = await getOnboardingStatus();
      setStatus(res.data);
    } catch (err) {
      console.error('Failed to load onboarding status:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F0FAF7]">
        <Loader2 className="w-8 h-8 animate-spin text-[#5BA4A4]" />
      </div>
    );
  }

  const isFullyApproved = status?.overall_status === 'fully_approved';

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F0FAF7] via-white to-[#E8F8F5]">
      <header className="bg-white/80 backdrop-blur-sm border-b border-[#A8E6CF]/30 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-gradient-to-br from-[#5BA4A4] to-[#A8E6CF] rounded-xl flex items-center justify-center">
              <Sprout className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-[#2D4A4A]">Onboarding</h1>
              <p className="text-xs text-[#5A7A7A]">Welcome, {user?.name}</p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate('/staff')}
            className="text-[#5A7A7A]"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Progress Overview */}
        <Card className="border border-[#A8E6CF]/30 bg-white/80">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="text-base font-semibold text-[#2D4A4A]">Onboarding Progress</h2>
                <p className="text-xs text-[#5A7A7A] mt-0.5">
                  {status?.completed_count || 0} of {status?.total_count || 12} sections approved
                </p>
              </div>
              {isFullyApproved ? (
                <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200" data-testid="fully-approved-badge">
                  <CheckCircle className="w-3 h-3 mr-1" /> Fully Approved
                </Badge>
              ) : (
                <span className="text-sm font-bold text-[#5BA4A4]">{status?.progress || 0}%</span>
              )}
            </div>
            <Progress value={status?.progress || 0} className="h-2.5" />
            {!isFullyApproved && (status?.submitted_count > 0) && (
              <p className="text-xs text-amber-600 mt-2">
                <Clock className="w-3 h-3 inline mr-1" />
                {status.submitted_count} section(s) awaiting admin review
              </p>
            )}
            {isFullyApproved && (
              <p className="text-xs text-emerald-600 mt-2">
                You are fully onboarded and cleared to work. You can now access training modules.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Section Checklist */}
        <div className="space-y-2">
          {(status?.sections || []).map((section, idx) => {
            const config = statusConfig[section.status] || statusConfig.not_started;
            const StatusIcon = config.icon;
            const isNext = !isFullyApproved && idx === (status?.sections || []).findIndex(s => s.status !== 'approved');

            return (
              <Card 
                key={section.id}
                className={`border transition-all cursor-pointer hover:shadow-md ${
                  isNext ? 'border-[#5BA4A4] ring-1 ring-[#5BA4A4]/20' : 'border-[#A8E6CF]/30'
                } ${section.status === 'approved' ? 'bg-emerald-50/50' : 'bg-white/80'}`}
                onClick={() => navigate(`/onboarding/${section.id}`)}
                data-testid={`onboarding-section-${section.id}`}
              >
                <CardContent className="p-4 flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                    section.status === 'approved' ? 'bg-emerald-100' : 
                    section.status === 'submitted' ? 'bg-amber-100' : 
                    section.status === 'in_progress' ? 'bg-blue-100' : 'bg-gray-100'
                  }`}>
                    <StatusIcon className={`w-4 h-4 ${
                      section.status === 'approved' ? 'text-emerald-600' : 
                      section.status === 'submitted' ? 'text-amber-600' : 
                      section.status === 'in_progress' ? 'text-blue-600' : 'text-gray-400'
                    }`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-medium text-[#2D4A4A] truncate">
                        {section.order}. {section.title}
                      </h3>
                      {isNext && (
                        <Badge className="bg-[#5BA4A4] text-white text-[10px] px-1.5 py-0 shrink-0">
                          Next
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-[#5A7A7A] truncate">{section.description}</p>
                    {section.admin_notes && section.status === 'in_progress' && (
                      <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" /> Admin feedback: {section.admin_notes}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge className={`text-[10px] ${config.color} border-0`}>
                      {config.label}
                    </Badge>
                    <ChevronRight className="w-4 h-4 text-[#5A7A7A]" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </main>
    </div>
  );
};

export default OnboardingDashboard;
